import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  inject,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import {
  MeetingAudience,
  MeetingAudienceVisibilityDto,
} from '@interfaces/meetings/responses/meeting-visibility.response';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { Observable, filter, finalize, first } from 'rxjs';
import { mensagemDoErro } from '@features/dashboard/meetings/components/meeting-presenter';
import {
  AUDIENCE_ICONS,
  AUDIENCE_ORDER,
  AUDIENCE_TITLES,
  CLIENT_RESTRICTION_LABELS,
  audienceState,
  displayReason,
  displayTitle,
  formatSecondsAsClock,
} from '@features/dashboard/meetings/components/meeting-visibility-presenter';

/** Uma linha da janela: a chave, o estado do backend e o que dá para fazer com ela. */
interface AudienceRow {
  audience: MeetingAudience;
  title: string;
  icon: string;
  state: MeetingAudienceVisibilityDto;
  /** Motivo exibido, sem o "Travada: " repetido (`displayReason`). */
  reason: string;
  /** "Decidido por Victor Tassinari em 28/10", "Sugestão do Cog", "Travada"... */
  origin: string | null;
  /** Decisão manual que pode voltar ao automático. */
  canReset: boolean;
}

/** Um motivo do Cog para barrar a call ao cliente, já em texto. */
interface ClientReasonRow {
  label: string;
  explanation: string;
  /** "04:10 – 05:02", quando o Cog apontou o trecho. */
  clock: string | null;
}

/** O que está gravando agora: uma chave, ou o Privar. */
type BusyTarget = MeetingAudience | 'private';

/**
 * Janela "Visibilidade da call" (card 3803, opção C do protótipo): as três chaves (Time, Parceiro,
 * Cliente) com o motivo de cada uma, a sugestão do Cog para o cliente, **Privar** / **Desfazer** e o
 * link para o painel de regras.
 *
 * Só exibe e pede: estado, motivo, trava e quem pode mexer vêm do backend (`MeetingVisibilityDto`).
 * Para quem não participou (`canManage = false`) abre só para leitura. Cada mudança devolve a reunião
 * atualizada em `changed`, para o cabeçalho trocar o selo.
 */
@Component({
  selector: 'app-modal-meeting-visibility',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './modal-meeting-visibility.component.html',
  styleUrl: './modal-meeting-visibility.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalMeetingVisibilityComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** A reunião mudou (chave, Privar ou Desfazer): a tela troca pelo que voltou. */
  readonly changed = output<MeetingDto>();
  /** Pediu o painel "Como funciona a visibilidade". */
  readonly openRules = output<void>();

  protected readonly meeting = signal<MeetingDto | null>(null);
  protected readonly busy = signal<BusyTarget | null>(null);
  /**
   * O `app-switch` guarda o próprio estado: clicado, ele vira sozinho mesmo se a API recusar.
   * Trocar a versão recria os switches com o estado real (mesma solução do toggle "Bot" da lista).
   */
  protected readonly switchVersion = signal(0);

  private readonly permissions = inject(PermissionsService);
  /** `MEETINGVISIBILITY_MANAGE` de quem está logado (regra de permissão por ação). */
  private readonly hasManagePermission = toSignal(
    this.permissions.hasPermission(Permissions.MeetingVisibility.MANAGE),
    { initialValue: false }
  );

  protected readonly visibility = computed(() => this.meeting()?.visibility ?? null);
  /** Participante ou admin interno (backend) e com a permissão da ação: muda chaves e usa o Privar. */
  protected readonly canManage = computed(
    () => !!this.visibility()?.canManage && this.hasManagePermission()
  );
  protected readonly title = computed(() => {
    const m = this.meeting();
    return m ? displayTitle(m) : '';
  });

  protected readonly rows = computed<AudienceRow[]>(() => {
    const visibility = this.visibility();
    if (!visibility) return [];
    return AUDIENCE_ORDER.map(audience => {
      const state = audienceState(visibility, audience);
      return {
        audience,
        title: AUDIENCE_TITLES[audience],
        icon: AUDIENCE_ICONS[audience],
        state,
        reason: displayReason(audience, state.reason),
        origin: this.describeOrigin(state),
        canReset: state.source === 'Manual' && state.canToggle && !visibility.isPrivate,
      };
    });
  });

  /** O Cog barrou a call para o cliente (a caixa do Cliente fica vermelha, como no protótipo). */
  protected readonly clientBlockedByAi = computed(
    () => this.visibility()?.clientState === 'BlockedByAi'
  );

  protected readonly clientReasons = computed<ClientReasonRow[]>(() =>
    (this.visibility()?.clientSuggestion?.reasons ?? []).map(reason => {
      const start = formatSecondsAsClock(reason.startSeconds);
      const end = formatSecondsAsClock(reason.endSeconds);
      return {
        label: CLIENT_RESTRICTION_LABELS[reason.kind] ?? CLIENT_RESTRICTION_LABELS.Other,
        explanation: reason.explanation,
        clock: start ? (end ? `${start} – ${end}` : start) : null,
      };
    })
  );

  /** "Privada por Victor Tassinari em 28/10" para a faixa do topo. */
  protected readonly privateText = computed(() => {
    const visibility = this.visibility();
    if (!visibility?.isPrivate) return null;
    const who = visibility.privateByName ? ` por ${visibility.privateByName}` : '';
    const when = visibility.privateAt
      ? ` em ${this.dates.formatDateTimeInFormat(visibility.privateAt, 'dd/MM')}`
      : '';
    return `Privada${who}${when}: só quem participou vê. Desfaça para cada chave voltar ao automático.`;
  });

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    translateService: TranslateService,
    private readonly meetingsService: MeetingsHttpService,
    private readonly confirmationService: ConfirmationService,
    private readonly toast: ToastHelperService,
    private readonly dates: DateFormatterService
  ) {
    super(translateService);
  }

  open(meeting: MeetingDto): void {
    this.meeting.set(meeting);
    this.busy.set(null);
    this.switchVersion.update(v => v + 1);
    this.dialogRef?.open('', '', '', 'Fechar', '', 'meeting-visibility', false, true);
  }

  /** Não há formulário: a janela grava a cada clique. Mantido pelo padrão de modal do projeto. */
  isFormValid(): boolean {
    return !!this.meeting() && !this.busy();
  }

  /** Não há submit: cada chave grava na hora. Fecha a janela. */
  submitAction(): void {
    this.close();
  }

  protected close(): void {
    this.dialogRef?.close();
  }

  protected showRules(): void {
    this.close();
    this.openRules.emit();
  }

  /** Liga ou desliga uma chave à mão. */
  protected toggle(audience: MeetingAudience, visible: boolean): void {
    const meeting = this.meeting();
    if (!meeting) return;
    this.switchVersion.update(v => v + 1);
    this.run(
      audience,
      this.meetingsService.setVisibility(meeting.id, audience, visible),
      `${AUDIENCE_TITLES[audience]}: ${visible ? 'liberado' : 'desligado'}.`
    );
  }

  /** Volta a chave ao automático (regra ou sugestão do Cog). */
  protected resetToAutomatic(audience: MeetingAudience): void {
    const meeting = this.meeting();
    if (!meeting) return;
    this.run(
      audience,
      this.meetingsService.setVisibility(meeting.id, audience, null),
      `${AUDIENCE_TITLES[audience]}: voltou ao automático.`
    );
  }

  /** Privar: confirmação curta e as três chaves desligam de uma vez. */
  protected makePrivate(): void {
    const meeting = this.meeting();
    if (!meeting) return;
    this.confirmationService
      .confirm(
        'Privar esta call?',
        `Só quem participou de "${this.title()}" continua vendo. Time, parceiro e cliente perdem o acesso até alguém desfazer.`,
        'danger',
        undefined,
        'Privar'
      )
      .pipe(
        first(),
        filter(confirmed => confirmed)
      )
      .subscribe(() =>
        this.run(
          'private',
          this.meetingsService.makePrivate(meeting.id),
          'Call privada: só quem participou vê.'
        )
      );
  }

  /** Desfaz o Privar: cada chave volta ao automático. */
  protected undoPrivate(): void {
    const meeting = this.meeting();
    if (!meeting) return;
    this.run(
      'private',
      this.meetingsService.undoPrivate(meeting.id),
      'Privar desfeito: cada chave voltou ao automático.'
    );
  }

  private run(target: BusyTarget, request: Observable<MeetingDto | null>, success: string): void {
    this.busy.set(target);
    request
      .pipe(
        first(),
        finalize(() => this.busy.set(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: updated => {
          this.toast.showToastSuccess(success);
          if (updated) {
            this.meeting.set(updated);
            this.changed.emit(updated);
          }
          this.switchVersion.update(v => v + 1);
        },
        error: error => {
          this.switchVersion.update(v => v + 1);
          this.toast.showToastError(
            'Não foi possível mudar a visibilidade',
            mensagemDoErro(error, 'Tente de novo em alguns instantes.')
          );
        },
      });
  }

  /** De onde veio o estado da chave, em texto curto. */
  private describeOrigin(state: MeetingAudienceVisibilityDto): string | null {
    switch (state.source) {
      case 'Manual': {
        const who = state.changedByName ? ` por ${state.changedByName}` : '';
        const when = state.changedAt
          ? ` em ${this.dates.formatDateTimeInFormat(state.changedAt, 'dd/MM')}`
          : '';
        return `Decidido${who}${when}`;
      }
      case 'Ai':
        return 'Segue a sugestão do Cog';
      case 'Lock':
        return 'Travada pela regra; nem a decisão manual muda';
      case 'Private':
        return 'Desligada pelo Privar';
      default:
        return null;
    }
  }
}
